import { useT } from "@transifex/react";
import type { ReactNode } from "react";
import type { SchoolClass, StudentTestContent } from "p6m-p6u";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import type { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { useSchoolClassOperations } from "../../hooks/useSchoolClassOperations";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";
import { CardOptionRow } from "./CardOptionRow";
import { ClassActivityCardExpandedContent } from "./ClassActivityCardExpandedContent";
import { SharedOnlineTestDetails } from "./SharedOnlineTestDetails";

export interface TestCardExpandedContentProps {
    testId: string;
    isShared: boolean;
    isSharedTest: boolean;
    sharedTestContent: StudentTestContent | null;
    availableSchoolClasses: SchoolClass[];
    onPreview: () => void;
    onShare: () => void;
    onCopyLink: () => void;
    onGrade: () => void;
    onEditDetails: () => void;
    onViewSubmissions: () => void;
    onConfigurePdf: () => void;
    onDownloadPdf: () => void;

    dateLabel?: string;
    selectedSchoolClassId?: string;
    visibilityBadge?: ReactNode;
}

export const TestCardExpandedContent = (props: TestCardExpandedContentProps) => {
    const t = useT();
    const { handleTestSchoolClassChange } = useSchoolClassOperations();

    const t_onlineTask = t("Online Task", { _tags: "AssistantOverview" });
    const t_preview = t("Preview", { _tags: "AssistantOverview" });
    const t_share = t("Share", { _tags: "AssistantOverview" });
    const t_configure = t("Configure", { _tags: "AssistantOverview" });
    const t_download = t("Download PDF", { _tags: "AssistantOverview" });

    const handleSchoolClassChange = (classId: string | null, targetClassVisibility?: SchoolClassVisibilities) => {
        const targetIsPrivate = isPrivateSchoolClass(targetClassVisibility);
        // Shared tests moving to a public class intentionally drop sharing (answers/evaluations wiped).
        const dropSharing = props.isSharedTest && !targetIsPrivate;
        const reassignData = props.isShared
            ? { sharedContent: dropSharing ? null : props.sharedTestContent }
            : undefined;
        return handleTestSchoolClassChange(props.testId, classId, reassignData);
    };

    const renderPreviewButton = () => (
        <AreaButton
            variant={ButtonVariant.SECONDARY}
            colorVariant={ButtonColorVariant.HIGH_CONTRAST}
            iconName="preview"
            iconPosition={IconPosition.RIGHT}
            role="button"
            onClick={props.onPreview}
        >
            {t_preview}
        </AreaButton>
    );

    return (
        <ClassActivityCardExpandedContent
            dateLabel={props.dateLabel}
            topLeftContent={props.visibilityBadge}
            topRightContent={renderPreviewButton()}
            selectedSchoolClassId={props.selectedSchoolClassId}
            availableSchoolClasses={props.availableSchoolClasses}
            onSchoolClassChange={handleSchoolClassChange}
            classActivityCategory={ClassActivityCategory.TEST}
            isShared={props.isShared}
            isSharedTest={props.isSharedTest}
        >
            {props.isShared ? (
                <SharedOnlineTestDetails
                    sharedTestContent={props.sharedTestContent}
                    onShare={props.onCopyLink}
                    onGrade={props.onGrade}
                    onEditDetails={props.onEditDetails}
                    onViewSubmissions={props.onViewSubmissions}
                />
            ) : (
                <CardOptionRow title={t_onlineTask}>
                    <AreaButton
                        data-testid="button-share-test"
                        variant={ButtonVariant.PRIMARY}
                        colorVariant={ButtonColorVariant.DEFAULT}
                        iconName="share"
                        iconPosition={IconPosition.RIGHT}
                        role="button"
                        onClick={props.onShare}
                    >
                        {t_share}
                    </AreaButton>
                </CardOptionRow>
            )}

            {/* PDF Section - Always visible */}
            <CardOptionRow title="PDF">
                <AreaButton
                    variant={ButtonVariant.SECONDARY}
                    colorVariant={ButtonColorVariant.HIGH_CONTRAST}
                    iconName="sliders"
                    iconPosition={IconPosition.RIGHT}
                    role="button"
                    onClick={props.onConfigurePdf}
                >
                    {t_configure}
                </AreaButton>
                <AreaButton
                    variant={ButtonVariant.PRIMARY}
                    iconName="download"
                    iconPosition={IconPosition.RIGHT}
                    role="button"
                    onClick={props.onDownloadPdf}
                >
                    {t_download}
                </AreaButton>
            </CardOptionRow>
        </ClassActivityCardExpandedContent>
    );
};
